import React, { useContext, useEffect, useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Typography,
  Divider,
  Paper,
  Autocomplete,
  Checkbox,
  ThemeProvider,
  Alert,
  Avatar,
} from "@mui/material";

import { getHeader, getUserName, useQuery } from "../../../../utility/utility";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import { ExpoTheme } from "../../../../theme/theme";
import MuPb from "../../../../widgets/MuPb";

import {
  API_ENDPOINT,
  EXHIBITOR,
  USER_ROLES,
  VISITOR,
} from "../../../../constants/constants";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { toast } from "react-toastify";
import axios from "axios";

import { getErrorResponse, toastError } from "../../../../utils2024/utils";

import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../context/UserContext";

import useApiEp from "../../../../hooks/useApiEp";
import BackBtn from "../../../../widgets/BackBtn";
import useGetEventSettings from "../../../hooks/useGetEventSettings";

function ScheduleMeeting() {
  const navigate = useNavigate();
  const query = useQuery();
  const tid = query.get("tid");

  const { user } = useContext(UserContext);

  // ==========================================================================
  // MEETING PEOPLE
  // ==========================================================================

  const { isLoading, isError, error, data } = useApiEp(
    `/user/get-meeting-people?id=${encodeURIComponent(tid)}`
  );

  // ==========================================================================
  // EVENT SETTINGS
  // ==========================================================================

  const {
    data: eventSettings,
    isLoading: eventSettingsLoading,
    isError: eventSettingsError,
    error: eventSettingsErrorData,
  } = useGetEventSettings();

  // ==========================================================================
  // STATE
  // ==========================================================================

  const [requesting, setRequesting] = useState(false);

  const [meeting_type, setMeetingType] = useState("");
  const [duration, setDuration] = useState("");

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedOtherMembers, setSelectedOtherMembers] = useState([]);

  const [membersData, setMembersData] = useState([]);
  const [otherMembersData, setOtherMembersData] = useState([]);

  /*
   * Don't hard-code the selected meeting date.
   *
   * Once event settings are loaded, this will be set
   * to the eventStartDate.
   */
  const [selectedDate, setSelectedDate] = useState(null);

  // ==========================================================================
  // EVENT DATE RANGE
  // ==========================================================================

  const parseEventDate = (value) => {
    if (!value) return null;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  };

  const eventStartDate = parseEventDate(eventSettings?.eventStartDate);

  const eventEndDate = parseEventDate(eventSettings?.eventEndDate);

  // ==========================================================================
  // SET INITIAL SELECTED DATE FROM EVENT START DATE
  // ==========================================================================

  useEffect(() => {
    if (!eventStartDate || !eventEndDate) {
      return;
    }

    /*
     * Only set automatically if nothing is selected.
     *
     * This prevents the user's manually selected date
     * from being reset if the component re-renders.
     */
    setSelectedDate((currentDate) => {
      if (currentDate) {
        return currentDate;
      }

      return eventStartDate;
    });
  }, [eventSettings?.eventStartDate, eventSettings?.eventEndDate]);

  // ==========================================================================
  // LOAD MEETING MEMBERS
  // ==========================================================================

  useEffect(() => {
    if (!data) {
      return;
    }

    // ------------------------------------------------------------------------
    // Current user's side
    // ------------------------------------------------------------------------

    if (data?.meeter?.role !== EXHIBITOR) {
      const mainContact = {
        id: data?.meeter?.username,
        name: `${data?.meeter?.firstName || ""} ${
          data?.meeter?.lastName || ""
        }`.trim(),
        profile_image: data?.meeter?.profile_image,
        email: data?.meeter?.email,
      };

      setSelectedMembers([mainContact]);
    } else if (data?.meeter?.role === EXHIBITOR) {
      const mainContact = {
        id: data?.meeter?.username,
        name: `${data?.meeter?.contactFirstName?.value || ""} ${
          data?.meeter?.contactLastName?.value || ""
        }`.trim(),
        profile_image: data?.meeter?.profile_image,
        email: data?.meeter?.companyEmail?.value,
      };

      setSelectedMembers([mainContact]);

      const membersArray = [mainContact];

      data?.members?.forEach((member) => {
        if (member.email !== mainContact.email) {
          membersArray.push({
            id: member._id,
            name: `${member.firstName || ""} ${member.lastName || ""}`.trim(),
            email: member.email,
            profile_image: member.profile_image,
          });
        }
      });

      setMembersData(membersArray);
    }

    // ------------------------------------------------------------------------
    // Other side
    // ------------------------------------------------------------------------

    if (data?.meeting?.role === EXHIBITOR) {
      const membersArray =
        data?.othermembers?.map((member) => ({
          id: member._id,
          name: `${member.firstName || ""} ${member.lastName || ""}`.trim(),
          email: member.email,
          profile_image: member.profile_image,
        })) || [];

      setOtherMembersData(membersArray);
    }

    if (data?.meeting?.role !== EXHIBITOR) {
      const member = {
        id: data?.meeting?.username,
        name: `${data?.meeting?.firstName || ""} ${
          data?.meeting?.lastName || ""
        }`.trim(),
        email: data?.meeting?.email,
        profile_image: data?.meeting?.profile_image,
      };

      setOtherMembersData([member]);
      setSelectedOtherMembers([member]);
    }
  }, [data]);

  // ==========================================================================
  // SUBMIT
  // ==========================================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedMembers.length) {
      toast.error("Please add at least one member.");
      return;
    }

    if (!selectedOtherMembers.length) {
      toast.error("Please select at least one person from the other side.");
      return;
    }

    if (!meeting_type) {
      toast.error("Please select a meeting type.");
      return;
    }

    if (!duration) {
      toast.error("Please select the duration of the meeting.");
      return;
    }

    if (!subject.trim()) {
      toast.error("Please enter a subject.");
      return;
    }

    if (!message.trim()) {
      toast.error("Please enter a message.");
      return;
    }

    if (!selectedDate) {
      toast.error("Please select a meeting date and time.");
      return;
    }

    // ------------------------------------------------------------------------
    // Extra protection
    // Even though DateTimePicker prevents invalid selection,
    // validate before sending to API.
    // ------------------------------------------------------------------------

    if (eventStartDate && selectedDate.getTime() < eventStartDate.getTime()) {
      toast.error("Meeting date cannot be before the event start date.");
      return;
    }

    if (eventEndDate && selectedDate.getTime() > eventEndDate.getTime()) {
      toast.error("Meeting date cannot be after the event end date.");
      return;
    }

    const dataToSubmit = {
      selectedDate,
      selectedMembers,
      selectedOtherMembers,
      meeting_type,
      duration,
      subject,
      message,
    };

    try {
      setRequesting(true);

      const { data: response } = await axios.post(
        `${API_ENDPOINT}/user/schedule-meeting?tid=${encodeURIComponent(tid)}`,
        dataToSubmit,
        getHeader()
      );

      toast.success(response.message);

      setTimeout(() => {
        navigate(`/${user?.role}/meetings`);
      }, 200);
    } catch (error) {
      toastError(error);
    } finally {
      setRequesting(false);
    }
  };

  // ==========================================================================
  // LOADING
  // ==========================================================================

  if (isLoading || eventSettingsLoading) {
    return <MuPb />;
  }

  // ==========================================================================
  // ERRORS
  // ==========================================================================

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  if (eventSettingsError) {
    return (
      <Alert severity="error">{getErrorResponse(eventSettingsErrorData)}</Alert>
    );
  }

  // ==========================================================================
  // INVALID EVENT DATE CONFIGURATION
  // ==========================================================================

  if (!eventStartDate || !eventEndDate) {
    return (
      <Alert severity="error">
        Event start date or event end date is not configured correctly.
      </Alert>
    );
  }

  if (eventStartDate.getTime() > eventEndDate.getTime()) {
    return (
      <Alert severity="error">
        Event start date cannot be after the event end date.
      </Alert>
    );
  }

  // ==========================================================================
  // UI
  // ==========================================================================

  return (
    <ThemeProvider theme={ExpoTheme}>
      <Box p={2}>
        <Box
          sx={{
            maxWidth: "1100px",
            ml: "auto",
            mr: "auto",
          }}
        >
          <BackBtn />

          <Typography fontWeight="bold" mt={2} variant="h5">
            Meeting Scheduler
          </Typography>

          <Box mt={2}>
            <Grid container spacing={2}>
              {/* ============================================================
                  MEETING WITH
              ============================================================ */}

              <Grid item xs={12}>
                <Typography fontSize="17px" mb={1}>
                  You are scheduling a meeting with
                </Typography>

                <Box display="flex">
                  <Avatar src={data?.meeting?.profile_image} />

                  <Box pl={2}>
                    <Typography fontWeight={600} fontSize="17px">
                      {data?.meeting && getUserName(data.meeting)}
                    </Typography>

                    <Typography fontWeight={500} fontSize="14px">
                      {data?.meeting?.role === USER_ROLES.EXHIBITOR
                        ? `${data?.meeting?.contactFirstName?.value || ""} ${
                            data?.meeting?.contactLastName?.value || ""
                          }`
                        : data?.meeting?.companyName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box mt={2} mb={2}>
                  <Divider />

                  <Typography mt={2} fontWeight="bold" variant="h5">
                    Meeting Data
                  </Typography>

                  <Typography fontSize="17px">
                    Please fill the following information for the meeting
                  </Typography>
                </Box>
              </Grid>

              {/* ============================================================
                  FORM
              ============================================================ */}

              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {/* ======================================================
                        LEFT BLOCK
                    ====================================================== */}

                    <Grid item xs={12} sm={6}>
                      <Grid container spacing={2}>
                        {/* YOUR SIDE */}

                        <Grid item xs={12}>
                          <Autocomplete
                            multiple
                            disabled={data?.meeter?.role !== EXHIBITOR}
                            value={selectedMembers}
                            options={membersData}
                            disableCloseOnSelect
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            getOptionLabel={(option) => option?.name || ""}
                            renderOption={(props, option, { selected }) => (
                              <li
                                {...props}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Checkbox
                                  icon={
                                    <CheckBoxOutlineBlankIcon fontSize="small" />
                                  }
                                  checkedIcon={
                                    <CheckBoxIcon fontSize="small" />
                                  }
                                  checked={selected}
                                  style={{
                                    marginRight: 8,
                                  }}
                                />

                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <span>{option.name}</span>

                                  <span
                                    style={{
                                      fontSize: "0.8em",
                                      color: "gray",
                                    }}
                                  >
                                    {option.email}
                                  </span>
                                </div>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Add who will join from your side*"
                                placeholder="Add member"
                              />
                            )}
                            onChange={(event, newValue) => {
                              setSelectedMembers(newValue);
                            }}
                          />
                        </Grid>

                        {/* OTHER SIDE */}

                        <Grid item xs={12}>
                          <Autocomplete
                            multiple
                            disabled={data?.meeting?.role === VISITOR}
                            value={selectedOtherMembers}
                            options={otherMembersData}
                            disableCloseOnSelect
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            getOptionLabel={(option) => option?.name || ""}
                            renderOption={(props, option, { selected }) => (
                              <li
                                {...props}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Checkbox
                                  icon={
                                    <CheckBoxOutlineBlankIcon fontSize="small" />
                                  }
                                  checkedIcon={
                                    <CheckBoxIcon fontSize="small" />
                                  }
                                  checked={selected}
                                  style={{
                                    marginRight: 8,
                                  }}
                                />

                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <span>{option.name}</span>

                                  <span
                                    style={{
                                      fontSize: "0.8em",
                                      color: "gray",
                                    }}
                                  >
                                    {option.email}
                                  </span>
                                </div>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Who you want to meet from other side*"
                                placeholder="Add member"
                              />
                            )}
                            onChange={(event, newValue) => {
                              setSelectedOtherMembers(newValue);
                            }}
                          />
                        </Grid>

                        {/* MEETING TYPE */}

                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Meeting Type*</InputLabel>

                            <Select
                              label="Meeting Type*"
                              value={meeting_type}
                              onChange={(e) => setMeetingType(e.target.value)}
                            >
                              <MenuItem disabled value="Online Meeting">
                                Online Meeting
                              </MenuItem>

                              <MenuItem value="OnSite Meeting">
                                OnSite Meeting
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* DURATION */}

                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel id="duration-label">
                              Duration of the meeting*
                            </InputLabel>

                            <Select
                              labelId="duration-label"
                              id="duration"
                              label="Duration of the meeting*"
                              value={duration}
                              onChange={(e) => setDuration(e.target.value)}
                            >
                              <MenuItem value={15}>15 mins</MenuItem>

                              <MenuItem value={30}>30 mins</MenuItem>

                              <MenuItem value={45}>45 mins</MenuItem>

                              <MenuItem value={60}>60 mins</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* ======================================================
                        RIGHT BLOCK
                    ====================================================== */}

                    <Grid item xs={12} sm={6}>
                      <Grid container spacing={2}>
                        {/* SUBJECT */}

                        <Grid item xs={12}>
                          <TextField
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            fullWidth
                            label="Subject"
                            required
                          />
                        </Grid>

                        {/* MESSAGE */}

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Message"
                            required
                            multiline
                            rows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            inputProps={{
                              maxLength: 200,
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* ======================================================
                        DATE / TIME
                    ====================================================== */}

                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          label="Select Date and Time*"
                          value={selectedDate}
                          onChange={(newValue) => {
                            setSelectedDate(newValue);
                          }}
                          /*
                           * Event-controlled calendar range
                           */
                          minDateTime={eventStartDate}
                          maxDateTime={eventEndDate}
                          sx={{
                            width: "100%",
                            mt: 2,
                          }}
                          renderInput={(params) => (
                            <TextField fullWidth required {...params} />
                          )}
                        />
                      </LocalizationProvider>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={1}
                      >
                        Meetings can only be scheduled during the event dates.
                      </Typography>
                    </Grid>

                    {/* ======================================================
                        SUBMIT
                    ====================================================== */}

                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        width="100%"
                        justifyContent="flex-end"
                        pt={2}
                        pb={2}
                      >
                        <LoadingButton
                          loading={requesting}
                          onClick={handleSubmit}
                          startIcon={<CalendarMonthIcon />}
                          type="submit"
                          variant="contained"
                          color="primary"
                        >
                          Send Request
                        </LoadingButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default ScheduleMeeting;
